/**
 * AI Category Badge Component
 * 
 * Displays a colored chip/badge showing the AI classification
 * of an email with confidence indicator
 */

import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import {
  LocalOffer as TagIcon,
  Person as CustomerIcon,
  LocalShipping as FornitorIcon,
  Forum as ComunicazioniIcon,
  Campaign as PubblicitaIcon,
  Psychology as AiIcon,
} from '@mui/icons-material';

// Category configuration with colors and labels
const CATEGORY_CONFIG = {
  clienti: {
    label: 'Cliente',
    color: 'success',
    bgColor: '#e8f5e9',
    textColor: '#2e7d32',
    icon: CustomerIcon,
  },
  fornitori: {
    label: 'Fornitore',
    color: 'info',
    bgColor: '#e3f2fd',
    textColor: '#1565c0',
    icon: FornitorIcon,
  },
  comunicazioni: {
    label: 'Comunicazione',
    color: 'default',
    bgColor: '#f3e5f5',
    textColor: '#7b1fa2',
    icon: ComunicazioniIcon,
  },
  pubblicita: {
    label: 'Pubblicità',
    color: 'warning',
    bgColor: '#fff3e0',
    textColor: '#e65100',
    icon: PubblicitaIcon,
  },
  // Legacy mappings for old data
  customer: {
    label: 'Cliente',
    color: 'success',
    bgColor: '#e8f5e9',
    textColor: '#2e7d32',
    icon: CustomerIcon,
  },
  advertising: {
    label: 'Pubblicità',
    color: 'warning',
    bgColor: '#fff3e0',
    textColor: '#e65100',
    icon: PubblicitaIcon,
  },
  spam: {
    label: 'Pubblicità',
    color: 'warning',
    bgColor: '#fff3e0',
    textColor: '#e65100',
    icon: PubblicitaIcon,
  },
  internal: {
    label: 'Comunicazione',
    color: 'default',
    bgColor: '#f3e5f5',
    textColor: '#7b1fa2',
    icon: ComunicazioniIcon,
  },
  other: {
    label: 'Comunicazione',
    color: 'default',
    bgColor: '#f3e5f5',
    textColor: '#7b1fa2',
    icon: ComunicazioniIcon,
  },
};

/**
 * Get confidence level description
 */
const getConfidenceLevel = (confidence) => {
  if (confidence >= 0.9) return { label: 'Molto alta', color: '#2e7d32' };
  if (confidence >= 0.75) return { label: 'Alta', color: '#558b2f' };
  if (confidence >= 0.5) return { label: 'Media', color: '#f9a825' };
  return { label: 'Bassa', color: '#ef6c00' };
};

/**
 * AiCategoryBadge - Displays email AI classification
 * 
 * @param {string} category - Classification category
 * @param {number} confidence - Confidence score 0-1
 * @param {string} rationale - Brief explanation (shown in tooltip)
 * @param {string} size - Chip size: 'small' | 'medium'
 * @param {boolean} showConfidence - Whether to show confidence indicator
 * @param {boolean} showIcon - Whether to show category icon
 * @param {function} onClick - Click handler
 */
export default function AiCategoryBadge({
  category,
  confidence,
  rationale,
  size = 'small',
  showConfidence = true,
  showIcon = true,
  onClick,
}) {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
  const confidenceLevel = getConfidenceLevel(confidence);
  const CategoryIcon = config.icon;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <AiIcon sx={{ fontSize: 14 }} />
        <strong>Classificazione AI</strong>
      </Box>
      <Box sx={{ fontSize: 12 }}>
        <div><strong>Categoria:</strong> {config.label}</div>
        {showConfidence && confidence !== undefined && (
          <div>
            <strong>Confidenza:</strong>{' '}
            <span style={{ color: confidenceLevel.color }}>
              {Math.round(confidence * 100)}% ({confidenceLevel.label})
            </span>
          </div>
        )}
        {rationale && (
          <div style={{ marginTop: 4, fontStyle: 'italic' }}>
            "{rationale}"
          </div>
        )}
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Chip
        size={size}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showIcon && <CategoryIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />}
            <span>{config.label}</span>
            {showConfidence && confidence !== undefined && (
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(0,0,0,0.1)',
                }}
              >
                {Math.round(confidence * 100)}%
              </Box>
            )}
          </Box>
        }
        sx={{
          bgcolor: config.bgColor,
          color: config.textColor,
          fontWeight: 500,
          '& .MuiChip-label': {
            px: 1,
          },
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            bgcolor: config.textColor,
            color: 'white',
          } : {},
        }}
        onClick={onClick}
      />
    </Tooltip>
  );
}

/**
 * AiPendingBadge - Shows that email is pending AI analysis
 */
export function AiPendingBadge({ size = 'small', onClick }) {
  return (
    <Tooltip title="Clicca per analizzare con AI" arrow>
      <Chip
        size={size}
        icon={<AiIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />}
        label="Analizza"
        variant="outlined"
        sx={{
          borderColor: '#9e9e9e',
          color: '#757575',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f5f5f5',
            borderColor: '#1976d2',
            color: '#1976d2',
          },
        }}
        onClick={onClick}
      />
    </Tooltip>
  );
}

/**
 * AiAnalyzingBadge - Shows that email is being analyzed
 */
export function AiAnalyzingBadge({ size = 'small' }) {
  return (
    <Chip
      size={size}
      icon={
        <AiIcon 
          sx={{ 
            fontSize: size === 'small' ? 14 : 18,
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }} 
        />
      }
      label="Analisi in corso..."
      sx={{
        bgcolor: '#e3f2fd',
        color: '#1565c0',
      }}
    />
  );
}

// Export category config for use in other components
export { CATEGORY_CONFIG, getConfidenceLevel };
